import React, { useState, useEffect } from 'react';
import { companyAPI } from '../../services/api';
import { updateCachedCompany } from '../common/useCompany';
import { setDisplayCurrency } from '../../utils/currency';
import toast from 'react-hot-toast';
import { Plus, Save, Star } from 'lucide-react';

/**
 * Multi-currency management: set the main (base) currency, add secondary
 * currencies, and maintain each secondary currency's exchange rate to the
 * base currency (stored as "1 {currency} = {rate} {base}").
 */
export default function CurrencySettings() {
  const [company, setCompany] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateInputs, setRateInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [newCur, setNewCur] = useState({ code: '', name: '', symbol: '' });

  const base = company?.base_currency || 'USD';

  const load = () => {
    companyAPI.current().then(setCompany).catch(e => toast.error(e.message));
    companyAPI.currencies().then(setCurrencies).catch(e => toast.error(e.message));
    companyAPI.exchangeRates().then(setRates).catch(e => toast.error(e.message));
  };
  useEffect(() => { load(); }, []);

  // Seed the rate inputs from the latest saved rate per currency pair.
  useEffect(() => {
    const map = {};
    currencies.forEach(c => {
      if (c.code === base) return;
      const latest = rates.find(r => r.from_currency === c.code && r.to_currency === base);
      map[c.code] = latest ? parseFloat(latest.rate) : '';
    });
    setRateInputs(map);
  }, [currencies, rates, base]); // eslint-disable-line

  const changeBase = async (code) => {
    if (code === base || !code) return;
    setBusy(true);
    try {
      await companyAPI.setBaseCurrency(code);
      // Apply instantly everywhere: shared formatter + cached company profile
      setDisplayCurrency(code);
      updateCachedCompany({ base_currency: code });
      toast.success(`Base currency changed to ${code}`);
      load();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const saveRate = async (code) => {
    const rate = parseFloat(rateInputs[code]);
    if (!rate || rate <= 0) { toast.error('Enter a valid exchange rate'); return; }
    setBusy(true);
    try {
      await companyAPI.createExchangeRate({
        from_currency: code, to_currency: base, rate,
        rate_date: new Date().toISOString().split('T')[0],
      });
      toast.success(`1 ${code} = ${rate} ${base} saved`);
      load();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const addCurrency = async (e) => {
    e.preventDefault();
    if (!newCur.code.trim() || !newCur.name.trim()) { toast.error('Code and name are required'); return; }
    setBusy(true);
    try {
      await companyAPI.addCurrency(newCur);
      toast.success('Currency added');
      setNewCur({ code: '', name: '', symbol: '' });
      load();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const others = currencies.filter(c => c.code !== base);

  return (
    <div>
      <div className="toolbar"><h2>Currencies &amp; Exchange Rates</h2></div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Main Currency (Base)</h3></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Base Currency — all accounts and reports are kept in this currency</label>
              <select className="form-control" value={base} onChange={e => changeBase(e.target.value)} disabled={busy}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Exchange Rates to {base}</h3></div>
        <div className="card-body" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr><th>Currency</th><th style={{ width: '38%' }}>Exchange Rate</th><th>Status</th><th className="text-right">Action</th></tr>
            </thead>
            <tbody>
              {currencies.map(c => (
                <tr key={c.code}>
                  <td>
                    <strong>{c.code}</strong> <span className="text-muted">{c.name} {c.symbol && `(${c.symbol})`}</span>
                  </td>
                  <td>
                    {c.code === base ? (
                      <span className="badge badge-posted">Base currency</span>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--desk-text-2)' }}>1 {c.code} =</span>
                        <input className="form-control" type="number" step="0.0001" min="0" style={{ width: 130 }}
                          value={rateInputs[c.code] ?? ''} placeholder="0.0000"
                          onChange={e => setRateInputs({ ...rateInputs, [c.code]: e.target.value })} />
                        <span style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--desk-text-2)' }}>{base}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    {c.code === base ? <span>—</span> : (
                      parseFloat(rateInputs[c.code]) > 0
                        ? <span className="badge badge-posted">Rate set</span>
                        : <span className="badge badge-warning">No rate</span>
                    )}
                  </td>
                  <td className="text-right">
                    {c.code !== base && (
                      <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => saveRate(c.code)}>
                        <Save size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Save Rate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {currencies.length === 0 && (
                <tr><td colSpan={4} className="empty-state"><h3>No currencies yet</h3></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Add Currency</h3></div>
        <div className="card-body">
          <form onSubmit={addCurrency} className="form-grid">
            <div className="form-group"><label>Code <span className="required">*</span></label>
              <input className="form-control" maxLength={3} placeholder="e.g. AFN" value={newCur.code} onChange={e => setNewCur({ ...newCur, code: e.target.value.toUpperCase() })} /></div>
            <div className="form-group"><label>Name <span className="required">*</span></label>
              <input className="form-control" placeholder="e.g. Afghan Afghani" value={newCur.name} onChange={e => setNewCur({ ...newCur, name: e.target.value })} /></div>
            <div className="form-group"><label>Symbol</label>
              <input className="form-control" maxLength={10} placeholder="e.g. ؋" value={newCur.symbol} onChange={e => setNewCur({ ...newCur, symbol: e.target.value })} /></div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                <Plus size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Add Currency
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
